import { CustomEvents } from '@/constants'
import db from '@/lib/db'

import type {
  SyncEvent,
  SyncEventRow,
  SyncEventsDatabase,
  SyncEventsStore,
  TransactionMode
} from './types'

const EVENTS_STORE = 'sync_events'
const LAST_SYNC_KEY = 'lastSyncTimestamp'

export class SyncLocalStorage {
  async saveEvents(events: SyncEvent[], synced: boolean): Promise<void> {
    await this.withEventsStore('readwrite', async (store) => {
      for (const event of events) {
        const row: SyncEventRow = { ...event, synced }
        await store.put(row, event.transaction_id)
      }
    })

    window.dispatchEvent(
      new CustomEvent(CustomEvents.SYNC_SAVED, { detail: events })
    )
  }

  async getUnsyncedEvents(): Promise<SyncEvent[]> {
    return this.withEventsStore('readonly', async (store) => {
      const result: SyncEvent[] = []
      let cursor = await store.openCursor()

      while (cursor) {
        const value = cursor.value as SyncEvent & { synced?: boolean }
        if (!value.synced) {
          result.push(value)
        }
        cursor = await cursor.continue()
      }

      return result
    })
  }

  async getAllEvents(): Promise<SyncEvent[]> {
    return this.withEventsStore('readonly', async (store) => {
      const result: SyncEvent[] = []
      let cursor = await store.openCursor()

      while (cursor) {
        result.push(cursor.value as SyncEvent)
        cursor = await cursor.continue()
      }

      return result
    })
  }

  async markEventsAsSynced(transaction_ids: string[]): Promise<void> {
    await this.withEventsStore('readwrite', async (store) => {
      for (const id of transaction_ids) {
        const value = await store.get(id)
        if (value) {
          value.synced = true
          await store.put(value, id)
        }
      }
    })
  }

  getLastSyncTimestamp(): number {
    const value = localStorage.getItem(LAST_SYNC_KEY)

    return value ? Number(value) : 0
  }

  setLastSyncTimestamp(timestamp: number): void {
    localStorage.setItem(LAST_SYNC_KEY, timestamp.toString())
  }

  async deleteEvent(transaction_id: string): Promise<void> {
    await this.withEventsStore('readwrite', async (store) => {
      await store.delete(transaction_id)
    })
  }

  private async ensureEventsDatabase(): Promise<SyncEventsDatabase> {
    const database = (await db.getDb()) as SyncEventsDatabase

    if (!database.objectStoreNames.contains(EVENTS_STORE)) {
      await db.recreateDatabase(`missing ${EVENTS_STORE}`)
      return db.getDb()
    }

    return database
  }

  private async withEventsStore<T>(
    mode: TransactionMode,
    executor: (store: SyncEventsStore) => Promise<T> | T
  ): Promise<T> {
    try {
      const database = await this.ensureEventsDatabase()
      const tx = database.transaction(EVENTS_STORE, mode)
      const store = tx.objectStore(EVENTS_STORE)
      const value = await executor(store)
      await tx.done
      return value
    } catch (error) {
      if (error?.name === 'NotFoundError') {
        await db.recreateDatabase(`NotFound during ${mode}`)
        return this.withEventsStore(mode, executor)
      }
      throw error
    }
  }
}
